// ---------------------------------------------------------------------------
// Fonts
// ---------------------------------------------------------------------------
/**
 * Font names a text style may carry. The PDF engines map each onto one of the
 * two bundled families (`FONT_FAMILY_MAP`); the editor and the signing page
 * preview text in the same family so what you see is what gets embedded.
 */
export const FONT_NAMES = ['Inter', 'Noto Sans', 'Sans', 'Serif', 'Noto Serif'];
export const FONT_FAMILY_MAP = {
    Inter: 'sans',
    'Noto Sans': 'sans',
    Sans: 'sans',
    Serif: 'serif',
    'Noto Serif': 'serif',
};
/** Which bundled family a style font name lands on (unknown/absent → sans). */
export function fontFamilyOf(font) {
    return (font && FONT_FAMILY_MAP[font]) || 'sans';
}
/** CSS stacks that preview text in the family the PDF embeds. */
export const FONT_FAMILY_CSS = {
    sans: "'DejaVu Sans', sans-serif",
    serif: "'DejaVu Serif', serif",
};
/** CSS font-family for previewing a style font name exactly as the PDF renders it. */
export function previewFontFamily(font) {
    return FONT_FAMILY_CSS[fontFamilyOf(font)];
}
// ---------------------------------------------------------------------------
// Text metrics
// ---------------------------------------------------------------------------
/** Line height as a multiple of font size — merge engine wrapping and editor preview alike. */
export const LINE_HEIGHT_RATIO = 1.15;
/**
 * Signer-entered text (text / date fields) starts at this size when the field
 * carries no fontSize of its own: 60% of the box height, clamped to 8–16pt.
 * The stamper then shrinks to fit the width.
 */
export const SIGNER_TEXT_MIN_PT = 8;
export const SIGNER_TEXT_MAX_PT = 16;
export const SIGNER_TEXT_HEIGHT_RATIO = 0.6;
export function signerTextFontSize(boxHeight, fontSize) {
    return (fontSize ??
        Math.min(SIGNER_TEXT_MAX_PT, Math.max(SIGNER_TEXT_MIN_PT, boxHeight * SIGNER_TEXT_HEIGHT_RATIO)));
}
// ---------------------------------------------------------------------------
// Check marks
// ---------------------------------------------------------------------------
/** The mark's ink fills this fraction of a checkbox field's short side. */
export const CHECK_INK_RATIO = 0.8;
/** Mark size for a field box — the rule the PDF engine, the editor and the signing page all apply. */
export const checkMarkSize = (w, h) => CHECK_INK_RATIO * Math.min(w, h);
// ---------------------------------------------------------------------------
// Typed signatures
// ---------------------------------------------------------------------------
/**
 * The look of a TYPED signature/initials. The signing page rasterises the
 * typed name with this font onto a canvas, and that image is what gets
 * stamped — so the editor previews the same face. DejaVu Serif leads the stack
 * because it is the serif the PDF engine embeds; Georgia is the fallback on
 * machines without it.
 */
export const TYPED_SIGNATURE_FONT = {
    family: "'DejaVu Serif', Georgia, serif",
    style: 'italic',
    color: '#0f172a',
};
/** Canvas the signing page renders a typed signature into before stamping. */
export const TYPED_SIGNATURE_CANVAS = { width: 600, height: 160, fontSize: 64 };
/** Preview font size for a typed signature in a box of this height (px at 100% zoom). */
export function typedSignatureFontSize(boxHeight) {
    return Math.min(boxHeight * 0.7, 40);
}
/** Flexbox justification matching the stamp engine's horizontal alignment (alignX). */
export function alignToJustify(align) {
    if (align === 'center')
        return 'center';
    if (align === 'right')
        return 'flex-end';
    return 'flex-start';
}
/**
 * Where a field's box sits on a page rendered at `scale` CSS px per PDF point
 * (1 at 100% zoom). Field geometry is top-left/pt, so this is a straight
 * multiply — kept in one place so every overlay lines up with the stamp.
 */
export function fieldBoxStyle(field, scale) {
    return {
        left: field.x * scale,
        top: field.y * scale,
        width: field.w * scale,
        height: field.h * scale,
    };
}
//# sourceMappingURL=rendering.js.map